// Classifies a player's chat line around surrender/mercy so the game can tell
// apart two opposite intents that previously collapsed into one:
//   - "beg"    : the PLAYER is begging the AI for mercy (self-directed plea)
//   - "demand" : the PLAYER is taunting / ordering the AI to surrender
//   - "none"   : neither
//
// The old code matched any surrender keyword and always granted the player
// mercy, so taunting the AI ("surrender!", "항복해", "humiliate you") was logged
// as the player pleading — an inverted result. Begging is checked first so that
// self-directed phrases ("i surrender", "항복할게") win over the imperative forms.

export type TauntIntent = "beg" | "demand" | "none";

// Player begging for mercy (self-directed). Korean self-plea endings and
// first-person English phrasing.
const BEG_KO = /살려|봐\s*줘|봐\s*주세요|봐주|제발|빌(게|어요|게요|께)|자비\s*(를|좀)?\s*(베풀|주|부탁)|항복\s*(할게|할래|합니다|할께|하겠|해요)|꿇을게|무릎\s*꿇을|졌(어|다|네|습니다|어요)/;
const BEG_EN = /\bmercy\b|spare\s*(me|us)|\bi\s*beg\b|forgive|please\s+(spare|stop|don'?t|have)|\b(i|we)\s*(surrender|give\s*up|yield|quit|lose|lost)\b/;

// Player demanding the AI surrender / taunting (other-directed, imperative).
const DEMAND_KO = /(항복|굴복)\s*(해라|해|하라|하세요|하시지|하지|시켜|시키)|꿇어(라)?|무릎\s*꿇(어|어라|려)|(너|너나|네가|니가)\s*(항복|굴복)|비웃|항복|굴복/;
const DEMAND_EN = /you\s+(surrender|give\s*up|lose|lost|yield|kneel|bow|are\s+done)|surrender\s*(now|right|!|\?)|\bsurrender\b|give\s*up|kneel|bow\s*(down|before)|yield\s*to\s*me|beg\s*me|humiliate|capitulate/;

// Phrases that look like begging but are actually taunts ("no mercy for you").
const NEGATED_BEG = /no\s+mercy|자비\s*(는)?\s*없|자비\s*따위/;

export function classifyTauntIntent(text: string): TauntIntent {
  if (!text) return "none";
  const raw = text.trim();
  if (!raw) return "none";
  const lower = raw.toLowerCase();

  const begs = !NEGATED_BEG.test(lower) && !NEGATED_BEG.test(raw) && (BEG_KO.test(raw) || BEG_EN.test(lower));
  if (begs) return "beg";

  if (DEMAND_KO.test(raw) || DEMAND_EN.test(lower)) return "demand";

  return "none";
}
